import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { CleaningShell } from "@/components/CleaningShell";
import { StatusPill } from "@/components/StatusPill";
import { BedDouble, ChevronRight, Building2, Users, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * PRIVACIDADE: usa exclusivamente /api/cleaning/quartos (DTO sem valores/CPF/telefone).
 */
export default function CleaningPanel({ user, onLogout }) {
  const navigate = useNavigate();
  const [quartos, setQuartos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroPousada, setFiltroPousada] = useState("all");
  const [filtroStatus, setFiltroStatus] = useState("all");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get("/cleaning/quartos");
      setQuartos(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar quartos.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const pousadas = useMemo(() => {
    const map = new Map();
    quartos.forEach((q) => { if (q.pousada_id) map.set(q.pousada_id, q.pousada_nome); });
    return Array.from(map, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [quartos]);

  const contadores = useMemo(() => {
    const c = { aguardando_limpeza: 0, em_limpeza: 0, aguardando_liberacao: 0, disponivel: 0, ocupado: 0, manutencao: 0 };
    quartos.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [quartos]);

  const filtrados = useMemo(() => {
    // Ordem: aguardando_limpeza > em_limpeza > aguardando_liberacao > ocupado > manutencao > disponivel
    const rank = {
      aguardando_limpeza: 0, em_limpeza: 1, aguardando_liberacao: 2,
      ocupado: 3, manutencao: 4, disponivel: 5,
    };
    return quartos
      .filter((q) => filtroPousada === "all" || q.pousada_id === filtroPousada)
      .filter((q) => filtroStatus === "all" || q.status === filtroStatus)
      .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
        || String(a.numero).localeCompare(String(b.numero), "pt-BR", { numeric: true }));
  }, [quartos, filtroPousada, filtroStatus]);

  return (
    <CleaningShell user={user} onLogout={onLogout}>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A652] mb-1 font-semibold">Meus quartos</div>
          <h1 className="font-serif text-3xl text-stone-900 leading-tight">Fila de limpeza</h1>
          <p className="text-xs text-stone-500 mt-1">Acompanhe e conclua as etapas obrigatórias antes de liberar cada quarto.</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="btn-cleaning-refresh"
          className="shrink-0 w-11 h-11 rounded-md border border-stone-200 flex items-center justify-center text-stone-600 hover:text-[#083A6B] hover:border-[#083A6B] transition-colors disabled:opacity-50"
          aria-label="Atualizar lista"
        >
          <RefreshCcw size={18} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Chips de resumo */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatusChip
          testid="chip-aguardando-limpeza"
          label="Aguardando"
          value={contadores.aguardando_limpeza || 0}
          color="text-[#B4472B] bg-[#FEE7DE] border-[#B4472B]/20"
          active={filtroStatus === "aguardando_limpeza"}
          onClick={() => setFiltroStatus(filtroStatus === "aguardando_limpeza" ? "all" : "aguardando_limpeza")}
        />
        <StatusChip
          testid="chip-em-limpeza"
          label="Em limpeza"
          value={contadores.em_limpeza || 0}
          color="text-[#1E4C7A] bg-[#DBE7F2] border-[#3671B0]/20"
          active={filtroStatus === "em_limpeza"}
          onClick={() => setFiltroStatus(filtroStatus === "em_limpeza" ? "all" : "em_limpeza")}
        />
        <StatusChip
          testid="chip-aguardando-liberacao"
          label="Liberar"
          value={contadores.aguardando_liberacao || 0}
          color="text-[#5E3B7A] bg-[#E9DFEF] border-[#7B4FA1]/20"
          active={filtroStatus === "aguardando_liberacao"}
          onClick={() => setFiltroStatus(filtroStatus === "aguardando_liberacao" ? "all" : "aguardando_liberacao")}
        />
      </div>

      {pousadas.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          <FilterPill active={filtroPousada === "all"} onClick={() => setFiltroPousada("all")} testid="filter-pousada-all">
            Todas
          </FilterPill>
          {pousadas.map((p) => (
            <FilterPill
              key={p.id}
              active={filtroPousada === p.id}
              onClick={() => setFiltroPousada(p.id)}
              testid={`filter-pousada-${p.id}`}
            >
              {p.nome}
            </FilterPill>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-stone-500 text-sm" data-testid="cleaning-loading">Carregando quartos…</div>
      ) : filtrados.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-stone-300 rounded-lg p-10 text-center">
          <BedDouble size={40} className="mx-auto text-stone-300 mb-3" strokeWidth={1.4} />
          <div className="text-stone-500 text-sm">Nenhum quarto encontrado para os filtros atuais.</div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="cleaning-quartos-list">
          {filtrados.map((q) => (
            <QuartoCard key={q.id} quarto={q} onOpen={() => navigate(`/limpeza/quartos/${q.id}`)} />
          ))}
        </ul>
      )}
    </CleaningShell>
  );
}

function QuartoCard({ quarto, onOpen }) {
  const hospede = quarto.hospede_atual;
  return (
    <li>
      <button
        onClick={onOpen}
        data-testid={`cleaning-card-${quarto.id}`}
        className="w-full text-left bg-[#FFFFFF] border border-stone-200 rounded-lg p-4 hover:border-[#083A6B] hover:shadow-sm transition-all"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 flex items-center gap-1 mb-1">
              <Building2 size={11} /> {quarto.pousada_nome}
            </div>
            <div className="font-serif text-2xl text-stone-900 leading-none">Quarto {quarto.numero}</div>
            <div className="text-xs text-stone-500 mt-1 capitalize">
              {quarto.tipo || "—"} · até {quarto.capacidade || 1} pax
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <StatusPill status={quarto.status} size="md" />
            <ChevronRight size={16} className="text-stone-300 mt-1" />
          </div>
        </div>
        {hospede && (
          <div className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-600 flex items-center gap-2">
            <Users size={12} />
            <span className="font-medium text-stone-800">{hospede.nome_curto || "Hóspede"}</span>
            <span className="text-stone-400">·</span>
            <span>
              {fmtDate(hospede.data_entrada)} → {fmtDate(hospede.data_saida)}
            </span>
            <span className="text-stone-400">·</span>
            <span>{hospede.num_pessoas || 1} pax</span>
          </div>
        )}
      </button>
    </li>
  );
}

function StatusChip({ label, value, color, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`border rounded-lg p-3 text-left transition-all ${color} ${active ? "ring-2 ring-offset-1 ring-current" : "opacity-90 hover:opacity-100"}`}
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
      <div className="font-serif text-2xl leading-none mt-1">{value}</div>
    </button>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-[#083A6B] text-white border-[#083A6B]"
          : "bg-white text-stone-600 border-stone-200 hover:border-[#083A6B]"
      }`}
    >
      {children}
    </button>
  );
}
